import type { Product } from './types'
import {
    ApplianceFeatures,
    ApplianceType,
    ApplianceVersion,
    AudioCodec,
    AudioLayout,
    CoaxPortMode,
    ComprimatoPortMode,
    EncoderFeatures,
    FecLimits,
    IpPortMode,
    MatroxPortMode,
    Nimbra400VideoFlags,
    RawVideo,
    RistProfile,
    SrtMode,
    VideoCodec,
    VideoFrameRate,
    VideonPortMode,
    VideonVideoFlags,
    ZixiMode,
} from './api/v1/types'
import { parseVaVersion, vaVersionIsGreaterOrEqual, VaVersions, vaVersionSupports12MTimeCode } from './vaVersion'
import { jsonParse, jsonStringify } from './serialization'
import { supportsFec, supportsZixiForEdgeConnect, supportsZixiZec } from './versions'
import { PRODUCTS } from './constants'

type ApplianceFeaturesFactory = (product: Product['id'], version?: ApplianceVersion) => ApplianceFeatures

export const standardFecSettings: FecLimits = {
    maxSize: 100,
    rowRange: {
        min: 4,
        max: 20,
    },
    colRange: {
        min: 1,
        max: 20,
    },
}

export enum VaGopStructure {
    IP = 'IP',
    IPB = 'IPB',
    IPBB = 'IPBB',
    IPBBB = 'IPBBB',
    IPBBBBB = 'IPBBBBB',
    IPBBBBBBB = 'IPBBBBBBB',
}

export enum Nimbra204GopStructure {
    IP = '0',
    IPB = '1',
    IPBB = '2',
    IPBBB = '3',
    IPBBBBB = '4',
    IPBBBBBBB = '5',
}

const nimbraVa220: ApplianceFeaturesFactory = (_product: Product['id'], version?: ApplianceVersion) => {
    const gopStructures: { name: string; value: string }[] = [
        { name: 'IP', value: VaGopStructure.IP },
        { name: 'IPB', value: VaGopStructure.IPB },
        { name: 'IPBB', value: VaGopStructure.IPBB },
        { name: 'IPBBB', value: VaGopStructure.IPBBB },
        { name: 'IPBBBBB', value: VaGopStructure.IPBBBBB },
        { name: 'IPBBBBBBB', value: VaGopStructure.IPBBBBBBB },
    ] satisfies { name: string; value: VaGopStructure }[]
    const sdiInputCapabilities: EncoderFeatures = {
        video: {
            customPid: true,
            codecs: [
                {
                    name: VideoCodec.h264,
                    bitrate: {
                        min: 1000000,
                        max: 30000000,
                    },
                    scte35Insertion: {
                        customPid: true,
                    },
                    gopStructures,
                },
                {
                    name: VideoCodec.h265,
                    bitrate: {
                        min: 1000000,
                        max: 30000000,
                    },
                    scte35Insertion: {
                        customPid: true,
                    },
                },
            ],
            flags: [
                ...(vaVersionSupports12MTimeCode(version?.vaVersion)
                    ? [
                          {
                              name: 'SMPTE 12M timecode',
                              value: Nimbra400VideoFlags.smpte12mTimecode,
                              disabledForCodecs: [VideoCodec.h265],
                          },
                      ]
                    : []),
            ],
        },
        audio: {
            maxAudioStreams: 8,
            layout: [AudioLayout.mono, AudioLayout.stereo],
            customPid: true,
            codecs: [
                {
                    name: AudioCodec.aes3,
                    bitratesKbps: [1920],
                },
                {
                    name: AudioCodec.aacLc,
                    bitratesKbps: [96, 128, 192, 256, 384],
                },
                {
                    name: AudioCodec.mpeg2,
                    bitratesKbps: [96, 128, 192, 256, 384],
                },
                {
                    name: AudioCodec.heAac,
                    bitratesKbps: [96, 128],
                },
            ].concat(
                vaVersionIsGreaterOrEqual(parseVaVersion(version && version.vaVersion), VaVersions['VA-16.4.0.0'])
                    ? [
                          {
                              name: AudioCodec.ac3pt,
                              bitratesKbps: [192, 384, 448, 640],
                          },
                      ]
                    : []
            ),
        },
    }
    return {
        proxy: {
            enabled: true,
            forwardedCookies: ['token'],
        },
        coaxPorts: 4,
        input: {
            modes: [
                { mode: IpPortMode.rtp },
                { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.listener, SrtMode.rendezvous] },
                { mode: IpPortMode.udp },
                { mode: IpPortMode.zixi, subModes: [ZixiMode.pull, ZixiMode.push] },
                { mode: CoaxPortMode.sdi, encoder: sdiInputCapabilities },
                { mode: CoaxPortMode.asi },
            ],
        },
        output: {
            modes: [
                { mode: IpPortMode.rtp },
                { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.listener, SrtMode.rendezvous] },
                { mode: IpPortMode.udp },
                { mode: IpPortMode.zixi, subModes: [ZixiMode.pull, ZixiMode.push] },
                { mode: CoaxPortMode.sdi },
                { mode: CoaxPortMode.asi },
            ],
        },
        fec: standardFecSettings,
    }
}

const nimbraVa225 = patchedCopy(nimbraVa220)
const nimbraVaDocker = patchedCopy(nimbraVa220, (v) => {
    v.coaxPorts = 0
    v.input = {
        ...v.input,
        modes: v.input?.modes.filter((m) => m.mode in IpPortMode) ?? [],
    }
    v.output = {
        ...v.output,
        modes: v.output?.modes.filter((m) => m.mode in IpPortMode) ?? [],
    }
})

const nimbra410 = patchedCopy(nimbraVaDocker)
const nimbra412 = patchedCopy(nimbraVa220, (v) => (v.coaxPorts = 2))
const nimbra414 = patchedCopy(nimbraVa220)

function patchedCopy<T>(
    factoryFn: (product: Product['id'], applianceVersion?: ApplianceVersion) => T,
    patch?: (v: T) => void
) {
    return (product: Product['id'], applianceVersion?: ApplianceVersion) => {
        const clone = jsonParse<T>(jsonStringify(factoryFn(product, applianceVersion)))
        patch?.(clone)
        return clone
    }
}

export const edgeConnect = (_product: Product['id'], version?: ApplianceVersion): ApplianceFeatures => {
    const zixiZecSupported = version ? supportsZixiForEdgeConnect(version) : false
    return {
        proxy: {
            enabled: false,
        },
        fec: version && supportsFec(ApplianceType.edgeConnect, version) ? standardFecSettings : undefined,
        input: {
            modes: [
                { mode: IpPortMode.generator },
                { mode: IpPortMode.rist, subModes: [RistProfile.simple] },
                { mode: IpPortMode.rtmp },
                { mode: IpPortMode.rtp },
                { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
                { mode: IpPortMode.udp },
                ...(zixiZecSupported ? [{ mode: IpPortMode.zixi, subModes: [ZixiMode.pull] }] : []),
            ],
        },
        output: {
            modes: [
                { mode: IpPortMode.rist, subModes: [RistProfile.simple] },
                { mode: IpPortMode.rtmp },
                { mode: IpPortMode.rtp },
                { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
                { mode: IpPortMode.udp },
                ...(zixiZecSupported ? [{ mode: IpPortMode.zixi, subModes: [ZixiMode.push] }] : []),
            ],
        },
    }
}

export const core = (product: Product['id'], version?: ApplianceVersion): ApplianceFeatures => {
    const zixiZecSupported = version ? supportsZixiZec(version) : false
    const zixiSupported = product === PRODUCTS.nimbraEdge.id || zixiZecSupported
    return {
        proxy: {
            enabled: false,
        },
        fec: version && supportsFec(ApplianceType.core, version) ? standardFecSettings : undefined,
        input: {
            modes: [
                { mode: IpPortMode.generator },
                { mode: IpPortMode.rist, subModes: [RistProfile.simple] },
                { mode: IpPortMode.rtmp },
                { mode: IpPortMode.rtp },
                { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
                { mode: IpPortMode.udp },
                ...(zixiSupported ? [{ mode: IpPortMode.zixi, subModes: [ZixiMode.pull] }] : []),
            ],
        },
        output: {
            modes: [
                { mode: IpPortMode.rist, subModes: [RistProfile.simple] },
                { mode: IpPortMode.rtmp },
                { mode: IpPortMode.rtp },
                { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
                { mode: IpPortMode.udp },
                ...(zixiSupported ? [{ mode: IpPortMode.zixi, subModes: [ZixiMode.push] }] : []),
            ],
        },
    }
}

export const emptyFeatureSet = (_product: Product['id'], _?: ApplianceVersion) => ({
    proxy: {
        enabled: false,
    },
})

const videon = (_product: Product['id'], _?: ApplianceVersion): ApplianceFeatures => {
    const inputCapabilities = {
        video: {
            latencyModes: [
                { name: 'High', value: 'HIGH' },
                { name: 'Normal', value: 'NORMAL' },
                { name: 'Low', value: 'LOW' },
                { name: 'Lowest', value: 'LOWEST' },
            ],
            scalingModes: [
                { name: 'Passthrough', value: 'RES_PASSTHROUGH' },
                { name: '320X180', value: 'RES_320X180' },
                { name: '480X270', value: 'RES_480X270' },
                { name: '640X360', value: 'RES_640X360' },
                { name: '720X576', value: 'RES_720X576' },
                { name: '960X540', value: 'RES_960X540' },
                { name: '1280X720', value: 'RES_1280X720' },
                { name: '1920X1080', value: 'RES_1920X1080' },
                { name: '3840X2160', value: 'RES_3840X2160' },
            ],
            customPid: false,
            codecs: [
                {
                    name: VideoCodec.h264,
                    profile: [
                        { name: 'Baseline Profile', value: 'PROFILE_BASELINE' },
                        { name: 'Main Profile', value: 'PROFILE_MAIN' },
                        { name: 'High Profile', value: 'PROFILE_HIGH' },
                    ],
                    bitrate: {
                        min: 1000000,
                        max: 30000000,
                    },
                },
                {
                    name: VideoCodec.h265,
                    bitrate: {
                        min: 1000000,
                        max: 30000000,
                    },
                    profile: [{ name: 'Main Profile', value: 'PROFILE_MAIN' }],
                },
            ],
            flags: [
                {
                    name: 'Limit to 30 FPS',
                    value: VideonVideoFlags.limitTo30Fps,
                    disabledForCodecs: [],
                },
                {
                    name: 'Enable SCTE-104/SCTE-35 Insertion',
                    value: VideonVideoFlags.scte35Insertion,
                    disabledForCodecs: [],
                },
                {
                    name: 'Enable KLV Timecode Insertion for Video Frames',
                    value: VideonVideoFlags.klvTimeCodeInsertion,
                    disabledForCodecs: [],
                },
                {
                    name: 'Enable SMPTE-2038',
                    value: VideonVideoFlags.smpte2038Insertion,
                    disabledForCodecs: [],
                },
                {
                    name: 'Enable CEA-708/608 captions',
                    value: VideonVideoFlags.cea708_608Captions,
                    disabledForCodecs: [],
                },
            ],
        },
        audio: {
            maxAudioStreams: 1,
            layout: [AudioLayout.stereo],
            customPid: false,
            codecs: [
                {
                    name: AudioCodec.aacLc,
                    bitratesKbps: [64, 96, 128, 192, 256, 320, 384, 512],
                },
            ],
        },
    }
    return {
        proxy: {
            enabled: false,
        },
        coaxPorts: 1,
        input: {
            modes: [
                {
                    prettyName: 'sdi',
                    mode: VideonPortMode.videonSdi,
                    encoder: inputCapabilities,
                },
                {
                    prettyName: 'hdmi',
                    mode: VideonPortMode.videonHdmi,
                    encoder: inputCapabilities,
                },
                {
                    prettyName: 'auto',
                    mode: VideonPortMode.videonAuto,
                    encoder: inputCapabilities,
                },
                { mode: IpPortMode.rtp },
                { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
                { mode: IpPortMode.udp },
            ],
        },
        output: {
            modes: [
                { mode: IpPortMode.rtp },
                { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
                { mode: IpPortMode.udp },
            ],
        },
    }
}

const dummyCapabilities: EncoderFeatures = {
    video: { codecs: [], customPid: false },
    audio: { maxAudioStreams: 1, layout: [], codecs: [], customPid: false },
}
const matroxCookies = [
    'Identity.External',
    'CommandCenter',
    'AspNetCore.Mvc.CookieTempDataProvider',
    '.AspNetCore.Antiforgery.*',
]
const matroxE4 = (_product: Product['id'], _?: ApplianceVersion): ApplianceFeatures => ({
    proxy: {
        enabled: true,
        forwardedCookies: matroxCookies,
    },
    coaxPorts: 4,
    input: {
        modes: [
            { mode: IpPortMode.rtp },
            { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
            { mode: IpPortMode.udp },
            { prettyName: 'sdi', mode: MatroxPortMode.matroxSdi, encoder: dummyCapabilities }, // Include 'encoder' to make NM render Matrox Encoder Settings component
        ],
    },
})
const matroxE410Bit = (_product: Product['id'], _?: ApplianceVersion): ApplianceFeatures => {
    const isRunningMatroxV2 = false // FIXME MG: Remove when we wish to support matrox v2 features
    return {
        proxy: {
            enabled: true,
            forwardedCookies: matroxCookies,
        },
        coaxPorts: 4,
        input: {
            modes: [
                { mode: IpPortMode.rtp },
                { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
                { mode: IpPortMode.udp },
                { prettyName: 'sdi', mode: MatroxPortMode.matroxSdi, encoder: dummyCapabilities }, // Include 'encoder' to make NM render Matrox Encoder Settings component
            ],
        },
        output: isRunningMatroxV2
            ? {
                  modes: [
                      { mode: IpPortMode.rtp },
                      { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
                      { mode: IpPortMode.udp },
                      { prettyName: 'sdi', mode: MatroxPortMode.matroxSdi, decoder: dummyCapabilities }, // Matrox decoder settings UI is embedded in the MatroxSdiPortForm component
                  ],
              }
            : undefined,
    }
}

const matroxD4 = (_product: Product['id'], _?: ApplianceVersion): ApplianceFeatures => ({
    proxy: {
        enabled: true,
        forwardedCookies: matroxCookies,
    },
    coaxPorts: 4,
    output: {
        modes: [
            { mode: IpPortMode.rtp },
            { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
            { mode: IpPortMode.udp },
            { prettyName: 'sdi', mode: MatroxPortMode.matroxSdi, decoder: dummyCapabilities }, // Matrox decoder settings UI is embedded in the MatroxSdiPortForm component
        ],
    },
})

const matroxS1 = (_product: Product['id'], _?: ApplianceVersion): ApplianceFeatures => ({
    proxy: {
        enabled: true,
        forwardedCookies: matroxCookies,
    },
    coaxPorts: 1,
    input: {
        modes: [
            { mode: IpPortMode.rtp },
            { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
            { mode: IpPortMode.udp },
            { prettyName: 'sdi', mode: MatroxPortMode.matroxSdi, encoder: dummyCapabilities }, // Include 'encoder' to make NM render Matrox Encoder Settings component
        ],
    },
    output: {
        modes: [
            { mode: IpPortMode.rtp },
            { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
            { mode: IpPortMode.udp },
            { prettyName: 'sdi', mode: MatroxPortMode.matroxSdi, decoder: dummyCapabilities }, // Matrox decoder settings UI is embedded in the MatroxSdiPortForm component
        ],
    },
})
const comprimato = (_product: Product['id'], _?: ApplianceVersion): ApplianceFeatures => {
    const inputCapabilities: EncoderFeatures = {
        video: {
            codecs: [
                {
                    name: VideoCodec.h264,
                    profile: [
                        { name: 'High', value: 'HIGH' },
                        { name: 'Main', value: 'MAIN' },
                        { name: 'Baseline (software encoder)', value: 'BASELINE' },
                    ],
                    colorSampling: [
                        { name: '4:2:0', value: '4:2:0' },
                        { name: '4:2:2', value: '4:2:2' },
                    ],
                    bitDepth: [8, 10],
                    resolution: [
                        { name: '720p', value: '720p' },
                        { name: '1080p', value: '1080p' },
                        { name: '480i', value: '480i' },
                        { name: '576i', value: '576i' },
                        { name: '1080i', value: '1080i' },
                    ],
                    scanRate: ['23.976', '24', '25', '29.97', '30', '50', '59.94', '60'],
                    bitrate: {
                        min: 0,
                        max: 40000000,
                    },
                    restrictions: {
                        bitDepth: {
                            determinedBy: 'profile',
                            mapping: {
                                BASELINE: [8],
                                MAIN: [8],
                                HIGH: [8, 10],
                            },
                        },
                        colorSampling: {
                            determinedBy: 'profile',
                            mapping: {
                                BASELINE: ['4:2:0'],
                                MAIN: ['4:2:0'],
                                HIGH: ['4:2:0', '4:2:2'],
                            },
                        },
                    },
                },
                {
                    name: VideoCodec.h265,
                    profile: [
                        { name: 'Main', value: 'MAIN' },
                        { name: 'Main10', value: 'MAIN10' },
                    ],
                    colorSampling: [{ name: '4:2:0', value: '4:2:0' }],
                    bitDepth: [8, 10],
                    resolution: [
                        { name: '720p', value: '720p' },
                        { name: '1080p', value: '1080p' },
                    ],
                    scanRate: ['23.976', '24', '25', '29.97', '30', '50', '59.94', '60'],
                    bitrate: {
                        min: 0,
                        max: 40000000,
                    },
                    restrictions: {
                        bitDepth: {
                            determinedBy: 'profile',
                            mapping: {
                                MAIN: [8],
                                MAIN10: [8, 10],
                            },
                        },
                        colorSampling: {
                            determinedBy: 'profile',
                            mapping: {
                                MAIN: ['4:2:0'],
                                MAIN10: ['4:2:0'],
                            },
                        },
                    },
                },
            ],
            customPid: false,
        },
        audio: {
            maxAudioStreams: 1,
            layout: [AudioLayout.stereo],
            customPid: false,
            codecs: [
                {
                    name: AudioCodec.aacAdts,
                    bitratesKbps: [64, 96, 128, 192, 256, 320, 384, 512],
                },
                {
                    name: AudioCodec.aacLatm,
                    bitratesKbps: [64, 96, 128, 192, 256, 320, 384, 512],
                },
                {
                    name: AudioCodec.mpeg2,
                    bitratesKbps: [64, 96, 128, 192, 256, 320, 384],
                },
                {
                    name: AudioCodec.aes3,
                    bitratesKbps: [],
                    bitDepth: [20, 24],
                },
                {
                    name: AudioCodec.ac3,
                    bitratesKbps: [192, 384, 448, 640],
                },
            ],
        },
    }

    return {
        proxy: {
            enabled: true,
            forwardedCookies: ['http_auth_session'],
        },
        input: {
            modes: [
                { mode: IpPortMode.rtp },
                { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
                { mode: IpPortMode.udp },
                { prettyName: 'sdi', mode: ComprimatoPortMode.comprimatoSdi, encoder: inputCapabilities },
                { prettyName: 'ndi', mode: ComprimatoPortMode.comprimatoNdi, encoder: inputCapabilities },
            ],
        },
        output: {
            modes: [
                { mode: IpPortMode.rtp },
                { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
                { mode: IpPortMode.udp },
                {
                    prettyName: 'sdi',
                    mode: ComprimatoPortMode.comprimatoSdi,
                    decoder: {
                        audio: {
                            maxAudioStreams: 1,
                            customPid: false,
                            layout: [AudioLayout.stereo],
                            codecs: [
                                {
                                    name: AudioCodec.aes3,
                                    bitratesKbps: [3000],
                                    bitDepth: [20, 24],
                                },
                                {
                                    name: AudioCodec.ac3,
                                    bitratesKbps: [192, 384, 448, 640],
                                },
                            ],
                        },
                        video: {
                            codecs: [
                                {
                                    name: RawVideo,
                                    colorSampling: [{ name: '4:2:2', value: '4:2:2' }],
                                    bitDepth: [10],
                                    bitrate: {
                                        min: 0,
                                        max: 40000000,
                                    },
                                    resolution: [
                                        { name: '720p', value: '720p' },
                                        { name: '1080p', value: '1080p' },
                                        { name: '480i', value: '480i' },
                                        { name: '576i', value: '576i' },
                                        { name: '1080i', value: '1080i' },
                                    ],
                                    scanRate: ['23.976', '24', '25', '29.97', '30', '50', '59.94', '60'],
                                },
                            ],
                            customPid: false,
                        },
                    },
                },
                {
                    prettyName: 'ndi',
                    mode: ComprimatoPortMode.comprimatoNdi,
                    decoder: {
                        audio: {
                            maxAudioStreams: 1,
                            layout: [AudioLayout.stereo],
                            customPid: false,
                            codecs: [
                                {
                                    name: AudioCodec.raw,
                                    bitratesKbps: [],
                                },
                            ],
                        },
                        video: {
                            codecs: [
                                {
                                    name: RawVideo,
                                    colorSampling: [{ name: '4:2:2', value: '4:2:2' }],
                                    bitDepth: [10],
                                    bitrate: {
                                        min: 0,
                                        max: 40000000,
                                    },
                                    resolution: [
                                        { name: '720p', value: '720p' },
                                        { name: '1080p', value: '1080p' },
                                        { name: '480i', value: '480i' },
                                        { name: '576i', value: '576i' },
                                        { name: '1080i', value: '1080i' },
                                    ],
                                    scanRate: ['23.976', '24', '25', '29.97', '30', '50', '59.94', '60'],
                                },
                            ],
                            customPid: false,
                        },
                    },
                },
            ],
        },
    }
}

const mediakindCookies = ['csrftoken', 'sessionid']
const mediakindHeaders = ['x-requested-with', 'x-csrftoken']
const mediakindRx1 = (_product: Product['id'], _?: ApplianceVersion): ApplianceFeatures => ({
    proxy: {
        enabled: true,
        forwardedCookies: mediakindCookies,
        forwardedHeaders: mediakindHeaders,
    },
    input: {
        modes: [
            { mode: IpPortMode.rtp },
            { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
            { mode: IpPortMode.udp },
        ],
    },
    output: {
        modes: [
            { mode: IpPortMode.rtp },
            { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
            { mode: IpPortMode.udp },
        ],
    },
})
const mediakindCe1 = (_product: Product['id'], _?: ApplianceVersion): ApplianceFeatures => ({
    proxy: {
        enabled: true,
        forwardedCookies: mediakindCookies,
        forwardedHeaders: mediakindHeaders,
    },
    input: {
        modes: [
            { mode: IpPortMode.rtp },
            { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
            { mode: IpPortMode.udp },
        ],
    },
    output: {
        modes: [
            { mode: IpPortMode.rtp },
            { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
            { mode: IpPortMode.udp },
        ],
    },
})

const nimbra204 = (_product: Product['id'], _?: ApplianceVersion): ApplianceFeatures => {
    const scanRates: VideoFrameRate[] = ['Follow source']
    const resolutions: { name: string; value: string }[] = [
        { name: '1280x720', value: '1280x720' },
        { name: '1920x1080', value: '1920x1080' },
        { name: '3840x2160', value: '3840x2160' },
    ]
    const gopStructures: { name: string; value: Nimbra204GopStructure }[] = [
        { name: 'IP', value: Nimbra204GopStructure.IP },
        { name: 'IPB', value: Nimbra204GopStructure.IPB },
        { name: 'IPBB', value: Nimbra204GopStructure.IPBB },
        { name: 'IPBBB', value: Nimbra204GopStructure.IPBBB },
        { name: 'IPBBBBB', value: Nimbra204GopStructure.IPBBBBB },
        { name: 'IPBBBBBBB', value: Nimbra204GopStructure.IPBBBBBBB },
    ]

    const pixelFormat = [
        { name: 'XV20: 4:2:2 10bit', value: 'XV20' },
        { name: 'XV15: 4:2:0 10bit', value: 'XV15' },
        { name: 'NV16 4:2:2 8bit', value: 'NV16' },
        { name: 'NV12 4:2:0 8bit', value: 'NV12' },
    ]

    const sdiInputCapabilities = {
        video: {
            customPid: false,
            codecs: [
                {
                    name: VideoCodec.h264,
                    profile: [{ name: 'High Profile', value: 'PROFILE_HIGH' }],
                    scanRate: scanRates,
                    resolution: resolutions,
                    bitrate: {
                        min: 1000000,
                        max: 30000000,
                    },
                    gopStructures,
                    pixelFormat,
                },
                {
                    name: VideoCodec.h265,
                    profile: [{ name: 'Main Profile', value: 'PROFILE_MAIN' }],
                    scanRate: scanRates,
                    resolution: resolutions,
                    bitrate: {
                        min: 1000000,
                        max: 30000000,
                    },
                    gopStructures,
                    pixelFormat,
                },
            ],
            flags: [],
        },
        audio: {
            maxAudioStreams: 4,
            layout: [AudioLayout.mono, AudioLayout.stereo],
            customPid: false,
            codecs: [
                {
                    name: AudioCodec.aacLc,
                    bitratesKbps: [32, 48, 64, 80, 96, 112, 128, 144, 160],
                },
                {
                    name: AudioCodec.mpeg2,
                    bitratesKbps: [32, 48, 56, 64, 80, 96, 112, 128, 144, 160, 192, 224, 256, 320, 384],
                    disabledForStreamIndices: [0],
                },
                {
                    name: AudioCodec.heAac,
                    bitratesKbps: [28, 32, 48, 64, 80, 96, 112, 128],
                },
                {
                    name: AudioCodec.heAacV2,
                    bitratesKbps: [16, 24, 32, 40, 48, 56, 64],
                },
                {
                    name: AudioCodec.ac3,
                    bitratesKbps: [
                        16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160, 180, 200, 220, 240, 260, 280, 300, 320,
                        360, 400, 440, 480, 520, 560, 600, 640,
                    ],
                    disabledForStreamIndices: [0],
                },
            ],
        },
    }
    return {
        proxy: {
            enabled: true,
            forwardedCookies: ['http_auth_session'],
            forwardedHeaders: [],
        },
        input: {
            modes: [
                { mode: IpPortMode.rtp },
                { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
                { mode: IpPortMode.udp },
                { prettyName: 'sdi', mode: CoaxPortMode.sdi, encoder: sdiInputCapabilities },
            ],
        },
        output: {
            modes: [
                { mode: IpPortMode.rtp },
                { mode: IpPortMode.srt, subModes: [SrtMode.caller, SrtMode.rendezvous, SrtMode.listener] },
                { mode: IpPortMode.udp },
            ],
        },
    }
}

export const applianceTypes: { [key in ApplianceType]: ApplianceFeaturesFactory } = {
    [ApplianceType.nimbraVA220]: nimbraVa220,
    [ApplianceType.nimbraVA225]: nimbraVa225,
    [ApplianceType.nimbraVAdocker]: nimbraVaDocker,
    [ApplianceType.nimbra410]: nimbra410,
    [ApplianceType.nimbra412]: nimbra412,
    [ApplianceType.nimbra414]: nimbra414,
    [ApplianceType.nimbra414b]: nimbra414,
    [ApplianceType.edgeConnect]: edgeConnect,
    [ApplianceType.core]: core,
    [ApplianceType.thumb]: emptyFeatureSet,
    [ApplianceType.videon]: videon,
    [ApplianceType.matroxMonarchEdgeE4_8Bit]: matroxE4,
    [ApplianceType.matroxMonarchEdgeE4_10Bit]: matroxE410Bit,
    [ApplianceType.matroxMonarchEdgeD4]: matroxD4,
    [ApplianceType.matroxMonarchEdgeS1]: matroxS1,
    [ApplianceType.comprimato]: comprimato,
    [ApplianceType.mediakindRx1]: mediakindRx1,
    [ApplianceType.mediakindCe1]: mediakindCe1,
    [ApplianceType.riversilica]: nimbra414, // TODO: Add riversilica features when we know what they are
    [ApplianceType.nimbra204]: nimbra204,
}
